"use client";
import { useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Typography,
} from "@mui/material";

import { editorColors } from "@/app/theme/palette";

type EditorFeedbackProps = {
	type: "hint" | "warning" | "success" | "error";
	message: string;
	bubble?: boolean;
	inline?: boolean;
	children?: React.ReactNode;
};

const BRANDFOLDER_STEPS = [
	"View asset",
	"Embed",
	"› Additional CDN Links",
	'"Asset CDN Link"',
	"Copy Link",
];

// Chevron icon (no @mui/icons-material dependency)
const ExpandIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="currentColor"
		aria-hidden="true"
	>
		<path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" />
	</svg>
);

const BubbleWarning = ({
	type,
	message,
}: Pick<EditorFeedbackProps, "type" | "message">) => {
	const { bg, border: borderColor, text: color } = editorColors[type];

	return (
		<Box
			role="alert"
			sx={{
				position: "absolute",
				bottom: "100%",
				left: 0,
				mb: "4px",
				zIndex: 10,
				whiteSpace: "normal",
				minWidth: 200,
				maxWidth: 500,
			}}
		>
			<Box
				sx={{
					bgcolor: bg,
					borderLeft: `4px solid ${borderColor}`,
					color,
					px: 1.5,
					py: 0.75,
					boxShadow: 3,
				}}
			>
				<Typography
					variant="body2"
					sx={{ color: "inherit", fontWeight: "bold" }}
				>
					{message}
				</Typography>
			</Box>
			<Box
				sx={{
					width: 0,
					height: 0,
					borderLeft: "8px solid transparent",
					borderRight: "8px solid transparent",
					borderTop: `8px solid ${bg}`,
					ml: "20px",
				}}
			/>
		</Box>
	);
};

export const EditorFeedback = ({
	type,
	message,
	bubble,
	inline,
	children,
}: EditorFeedbackProps) => {
	const [expanded, setExpanded] = useState(false);

	if (bubble) {
		return (
			<Box sx={{ position: "relative" }}>
				<BubbleWarning type={type} message={message} />
				{children}
			</Box>
		);
	}

	if (inline) {
		return (
			<Box
				role="alert"
				sx={{
					bgcolor: editorColors[type].bg,
					borderLeft: `4px solid ${editorColors[type].border}`,
					color: editorColors[type].text,
					px: 1.5,
					py: 0.75,
					width: "100%",
				}}
			>
				<Typography
					variant="body2"
					sx={{ color: "inherit", fontWeight: "bold" }}
				>
					{message}
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			role="alert"
			sx={{
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				zIndex: 10,
				bgcolor: editorColors[type].bg,
				borderLeft: `4px solid ${editorColors[type].border}`,
				color: editorColors[type].text,
				boxShadow: 3,
			}}
		>
			<Accordion
				expanded={expanded}
				disableGutters
				elevation={0}
				sx={{ bgcolor: "transparent", color: "inherit" }}
			>
				<AccordionSummary
					expandIcon={<ExpandIcon />}
					onClick={() => setExpanded((prev) => !prev)}
					title='To get an Asset CDN Link from Brandfolder: View asset → Embed → › Additional CDN Links → "Asset CDN Link" → Copy Link'
					sx={{
						cursor: "pointer",
						minHeight: 0,
						px: 1.5,
						py: 0.75,
						color: "inherit",
						"& .MuiAccordionSummary-expandIconWrapper": { color: "inherit" },
					}}
				>
					<Typography
						variant="body2"
						sx={{ color: "inherit", fontWeight: "bold" }}
					>
						{message}
					</Typography>
				</AccordionSummary>
				<AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
					<Typography variant="body2" sx={{ mb: 0.75, opacity: 0.9 }}>
						To get an Asset CDN Link from Brandfolder:
					</Typography>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 0.5,
						}}
					>
						{BRANDFOLDER_STEPS.map((step, i) => (
							<Box
								key={step}
								sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
							>
								<Box
									component="span"
									sx={{
										px: 0.75,
										py: 0.25,
										borderRadius: 1,
										bgcolor: "rgba(0,0,0,0.15)",
										fontFamily: "monospace",
										fontSize: "0.75rem",
										whiteSpace: "nowrap",
									}}
								>
									{step}
								</Box>
								{i < BRANDFOLDER_STEPS.length - 1 && (
									<Typography
										component="span"
										variant="body2"
										sx={{ opacity: 0.7 }}
									>
										→
									</Typography>
								)}
							</Box>
						))}
					</Box>
				</AccordionDetails>
			</Accordion>
		</Box>
	);
};
